import { setupSchema } from '@tasks/contracts/auth';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { formValue } from '#lib/form-value';
import { authSession } from '#lib/auth/auth-session';
import { runSetup, setupPending } from '#lib/auth/auth-api';
import { Button } from '#components/ui/button';

const readForm = (form: FormData) => ({
  workspace: { slug: formValue(form, 'slug'), name: formValue(form, 'workspace') },
  admin: {
    email: formValue(form, 'email'),
    handle: formValue(form, 'handle'),
    name: formValue(form, 'name'),
    password: formValue(form, 'password'),
  },
});

const Field = ({ name, label, type = 'text' }: { name: string; label: string; type?: string }) => (
  <label className="block text-sm">
    {label}
    <input
      name={name}
      type={type}
      required
      className="mt-1 w-full rounded-sm border border-input px-3 py-2 bg-background"
    />
  </label>
);

const SetupPage = () => {
  const pending = useQuery({ queryKey: ['setup'], queryFn: setupPending, retry: false });
  const [error, setError] = useState<string | null>(null);

  if (pending.isLoading) return null;
  if (pending.data === false) return <Navigate to="/login" replace />;

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const parsed = setupSchema.safeParse(readForm(form));
    if (!parsed.success) {
      setError(
        'Check the details: the handle accepts lowercase letters, digits, hyphen and underscore.',
      );
      return;
    }
    try {
      authSession.accept(await runSetup(parsed.data, formValue(form, 'token')));
    } catch {
      setError('Could not set up. Check the setup token.');
    }
  };

  return (
    <main className="mx-auto max-w-md p-6">
      <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-raised">
        <h1 className="text-2xl">Set up Tasks</h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">
          One time only. The token is the value of <code>SETUP_TOKEN</code> on the server.
        </p>
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
          <Field name="token" label="Setup token" type="password" />
          <Field name="workspace" label="Workspace name" />
          <Field name="slug" label="Short identifier" />
          <Field name="name" label="Your name" />
          <Field name="handle" label="Your handle (for mentions)" />
          <Field name="email" label="Your email" type="email" />
          <Field name="password" label="Your password" type="password" />
          {error === null ? null : (
            <p role="alert" className="text-sm text-destructive-text">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full">
            Set up
          </Button>
        </form>
      </div>
    </main>
  );
};

export const Route = createFileRoute('/setup')({ component: SetupPage });
